import React, { useState, useEffect } from 'react';
import { useSelector, shallowEqual } from 'react-redux';
import { wrapFunctionalComponent } from 'utils/framework';
import { Box, Link } from 'components/ui';
import localeUtils from 'utils/LanguageLocale';
import resourceWrapper from 'utils/framework/resourceWrapper';
import ColorIQBadge from 'components/ProductPage/ColorIQBadge/ColorIQBadge';
import Location from 'utils/Location';
import ShadeFinderTextEN from 'components/ProductPage/ShadeFinderText/locales/ShadeFinderText_en_US';
import ShadeFinderTextFR from 'components/ProductPage/ShadeFinderText/locales/ShadeFinderText_fr_CA';

const FOUNDATION_CATEGORY_URL = '/shop/foundation-makeup';
const getText = resourceWrapper(localeUtils.getLocalisedDataFromResource(ShadeFinderTextEN, ShadeFinderTextFR));

const deriveWizardState = (wizard, pageProduct) => {
    const wizardMatchText = wizard?.matchText ?? null;
    const wizardSkuId = wizard?.result?.skuId ?? null;
    const currentSkuId = pageProduct?.currentSku?.skuId ?? null;
    const isMatch = !!(wizardSkuId && currentSkuId && wizardSkuId === currentSkuId);

    return { wizardMatchText, wizardSkuId, isMatch };
};

const ShadeFinderText = ({ product, colorIQMatch }) => {
    const wizard = useSelector(state => state.wizard, shallowEqual);
    const pageProduct = useSelector(state => state.page.product, shallowEqual);

    // Preserve class SSR/first-render behavior: original returned null on first render
    // (state initialized empty; wizard was subscribed inside componentDidMount).
    // Deriving from Redux synchronously on first render would change SSR output when
    // state.wizard already has data.
    const [mounted, setMounted] = useState(false);
    useEffect(() => {
        setMounted(true);
    }, []);

    if (!mounted) {
        return null;
    }

    const { wizardMatchText, wizardSkuId, isMatch } = deriveWizardState(wizard, pageProduct);

    const noMatch = 'no match';
    const noColorIQNoMatch = 'No Match';

    const matchText = wizardMatchText && wizardMatchText.toLowerCase();
    const shadeFinderResultText = matchText === noMatch ? getText('noMatches') : getText('matchFound');

    if (colorIQMatch && colorIQMatch.skuId && colorIQMatch.skuId !== noColorIQNoMatch && matchText !== noMatch && !wizardSkuId) {
        return (
            <ColorIQBadge
                colorIQMatch={colorIQMatch}
                product={product}
                isColorIQ={false}
            />
        );
    } else {
        if (!matchText || (!isMatch && matchText !== noMatch)) {
            return null;
        } else {
            return (
                <Box
                    marginRight='auto'
                    paddingX='.5em'
                    paddingY='.125em'
                    fontSize='sm'
                    lineHeight='tight'
                    backgroundColor='nearWhite'
                    borderRadius={2}
                >
                    {shadeFinderResultText}
                    {matchText === noMatch && (colorIQMatch.skuId === noColorIQNoMatch || !colorIQMatch.skuId) && (
                        <React.Fragment>
                            {' '}
                            <Link
                                display='inline'
                                padding={1}
                                margin={-1}
                                href={FOUNDATION_CATEGORY_URL}
                                onClick={e => {
                                    Location.navigateTo(e, FOUNDATION_CATEGORY_URL);
                                }}
                                color='blue'
                                children={getText('seeAllFoundations')}
                            />
                        </React.Fragment>
                    )}
                    {colorIQMatch?.skuId && colorIQMatch?.skuId !== noColorIQNoMatch && (
                        <React.Fragment>
                            {' '}
                            <ColorIQBadge
                                colorIQMatch={colorIQMatch}
                                product={product}
                                isColorIQ={true}
                            />
                        </React.Fragment>
                    )}
                </Box>
            );
        }
    }
};

export default wrapFunctionalComponent(ShadeFinderText, 'ShadeFinderText');
